import { PricingInsightsResultDTO } from '../../../data/CarrierPayDTO';
import { Tab, Tabs } from '@material-ui/core';
import { useMemo, useState } from 'react';
import { Column, Columns, Inline, Stack } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import emptyLoadsImg from './assets/empty_loads.png';
import emptyLoadsDarkImg from './assets/empty_loads_dark.png';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { RecentMovedLoad } from './RecentMovedLoad';
import { RecentPosting } from './RecentPosting';
import { trackCarrierPayEvent } from '../../../data/CarrierPayAnalytics';
import { ContactSalesForm } from '../unlock/ContactSalesForm';
import { Image } from '@superdispatch/ui';
import { Blurred } from '../unlock/Blurred';

interface Props {
  pricingInsights: PricingInsightsResultDTO;
  isLocked: boolean;
}

export function PricingRecommendationTabs({
  pricingInsights,
  isLocked,
}: Props) {
  const [tab, $tab] = useState<'recent_postings' | 'recent_moves'>(
    'recent_postings',
  );
  const list = pricingInsights[tab];

  const [averagePrice, averagePricePerMile] = useMemo(() => {
    let totalPrice = 0;
    let totalPricePerMile = 0;

    if (!list.length) {
      return [0, 0];
    }

    for (let item of list) {
      totalPrice += item.price_per_vehicle;
      totalPricePerMile += item.price_per_mile_per_vehicle;
    }

    return [totalPrice / list.length, totalPricePerMile / list.length];
  }, [list]);

  return (
    <Stack space="small">
      <Box borderBottomWidth="small" borderBottomColor="Silver400">
        <Tabs
          value={tab}
          onChange={(_, value) => {
            $tab(value);
            trackCarrierPayEvent({
              name: 'Pricing Insights: UI Interaction',
              type:
                value === 'recent_moves'
                  ? 'Clicked Recent Moves'
                  : 'Clicked Recent Postings',
            });
          }}
        >
          <Tab value="recent_postings" label="Posted to Super Loadboard" />
          <Tab value="recent_moves" label="Recent Moves" />
        </Tabs>
      </Box>

      <Columns space={['small', 'small', 'large']} collapseBelow="desktop">
        <Column width="content">
          <Inline space={['xsmall', 'large']}>
            <Inline>
              <TextBox color="secondary">Avg price/vehicle</TextBox>
              <Blurred $disabled={!isLocked}>
                <TextBox>
                  {formatCurrency(averagePrice, {
                    maximumFractionDigits: 0,
                  })}
                </TextBox>
              </Blurred>
            </Inline>

            <Inline>
              <TextBox color="secondary">Avg price/mi</TextBox>
              <Blurred $disabled={!isLocked}>
                <TextBox>
                  {formatCurrency(averagePricePerMile, {
                    maximumFractionDigits: 2,
                  })}
                </TextBox>
              </Blurred>
            </Inline>
          </Inline>
        </Column>
      </Columns>

      {tab === 'recent_postings' &&
        (isLocked ? (
          <ContactSalesForm />
        ) : list.length === 0 ? (
          <Box marginTop={['none', 'xxlarge']} paddingTop={['none', 'large']}>
            <Stack align="center">
              <img src={emptyLoadsImg} alt="" width="70" />
              <TextBox color="secondary" variant="body-semibold" align="center">
                Currently, there are no posted loads for this route. <br />
                Change the route and try again.
              </TextBox>
            </Stack>
          </Box>
        ) : (
          <Stack>
            {pricingInsights.recent_postings.map((item, index) => (
              <RecentPosting key={index} posting={item} />
            ))}
          </Stack>
        ))}

      {tab === 'recent_moves' &&
        (isLocked ? (
          <ContactSalesForm />
        ) : list.length === 0 ? (
          <Box marginTop={['none', 'xxlarge']} paddingTop={['none', 'large']}>
            <Stack align="center">
              <Image
                src={emptyLoadsImg}
                srcDark={emptyLoadsDarkImg}
                alt=""
                width="70"
              />
              <TextBox color="secondary" variant="body-semibold" align="center">
                Currently, there are no recently delivered loads. <br /> Change
                the route and try again.
              </TextBox>
            </Stack>
          </Box>
        ) : (
          <Stack>
            {pricingInsights.recent_moves.map((item, index) => (
              <RecentMovedLoad key={index} load={item} />
            ))}
          </Stack>
        ))}
    </Stack>
  );
}
